import { useEffect, useRef, useState } from 'react'
import { getCategoryColor } from '../lib/colors'
import type { Tag } from '../hooks/useTags'

const COLOR_KEYS = ['blue', 'purple', 'green', 'red', 'amber', 'orange', 'pink', 'cyan', 'lime', 'violet']

interface TagPickerProps {
  conversationUrn: string
  tags: Tag[]
  assignedTagIds: string[]
  onAssign: (tagId: string, conversationUrn: string) => void
  onRemove: (tagId: string, conversationUrn: string) => void
  onCreateTag: (name: string, color: string) => Promise<Tag | null | undefined>
  onClose: () => void
}

export default function TagPicker({ conversationUrn, tags, assignedTagIds, onAssign, onRemove, onCreateTag, onClose }: TagPickerProps) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose()
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const filtered = query.trim()
    ? tags.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()))
    : tags

  const assignedSet = new Set(assignedTagIds)
  const exactMatch = tags.some((t) => t.name.toLowerCase() === query.toLowerCase().trim())
  const showCreate = query.trim() && !exactMatch

  const handleToggle = (tag: Tag) => {
    if (assignedSet.has(tag.id)) {
      onRemove(tag.id, conversationUrn)
    } else {
      onAssign(tag.id, conversationUrn)
    }
  }

  const handleCreate = async () => {
    const name = query.trim()
    if (!name) return
    const nextColor = COLOR_KEYS[tags.length % COLOR_KEYS.length]
    const newTag = await onCreateTag(name, nextColor)
    if (newTag?.id) {
      onAssign(newTag.id, conversationUrn)
    }
    setQuery('')
  }

  return (
    <div ref={containerRef} className="absolute left-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-lg">
      {/* Search input */}
      <div className="border-b border-zinc-100 px-3 py-2">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && showCreate) handleCreate() }}
          placeholder="Search or create..."
          className="w-full text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
      </div>

      {/* Tag list */}
      <div className="max-h-48 overflow-y-auto">
        {filtered.map((tag) => (
          <button
            key={tag.id}
            onClick={() => handleToggle(tag)}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left hover:bg-zinc-50"
          >
            <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-zinc-200">
              {assignedSet.has(tag.id) && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            {tag.icon ? (
              <span className="w-3 shrink-0 text-center text-[11px] leading-none" aria-hidden="true">{tag.icon}</span>
            ) : (
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: getCategoryColor(tag.color) }} />
            )}
            <span className="flex-1 truncate text-xs text-zinc-700">{tag.name}</span>
            {tag.team_id && (
              <svg
                width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="shrink-0 text-zinc-400"
                aria-label="Shared with team"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            )}
          </button>
        ))}

        {/* Create new tag */}
        {showCreate && (
          <button
            onClick={handleCreate}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-blue-600 hover:bg-blue-50"
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center text-sm">+</span>
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: getCategoryColor(COLOR_KEYS[tags.length % COLOR_KEYS.length]) }} />
            <span className="truncate text-xs">Create "{query.trim()}"</span>
          </button>
        )}

        {filtered.length === 0 && !showCreate && (
          <p className="px-3 py-3 text-center text-[11px] text-zinc-400">No tags</p>
        )}
      </div>
    </div>
  )
}
